import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, RefreshCw, Mail, Download, Loader2, AlertTriangle, DollarSign, CheckCircle2, XCircle, Clock, Copy,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Stat = ({ icon: Icon, label, count, amount, tone, testId }) => {
  const tones = {
    green: 'bg-green-50 border-green-200 text-green-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    gray: 'bg-white border-[#E5E7EB] text-[#0A0A0A]',
  };
  return (
    <div className={`border rounded-lg p-3 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] uppercase tracking-wide font-bold">{label}</p>
      </div>
      <div className="font-heading text-2xl font-bold">{count}</div>
      <p className="text-xs font-mono">{fmt(amount)}</p>
    </div>
  );
};

/**
 * Payment Reconciliation Panel — Phase 3 admin tools.
 * Provides:
 *  - Live summary (paid/pending/failed/refunded/expired/duplicate + net revenue)
 *  - "Clean Up Pending Transactions" — reconciles old pending records against Stripe
 *  - "Send Reconciliation Email" — emails a formatted report to admin team
 *  - CSV export
 *  - Suspicious activity alerts surfaced inline
 */
const PaymentReconciliationPanel = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/payments/reconciliation-summary`, { withCredentials: true });
      setSummary(r.data);
    } catch (e) {
      setSummary(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const runCleanup = async () => {
    if (!window.confirm('Run cleanup? This will check Stripe for every pending record older than 30 minutes and reconcile its true status. Safe to run anytime.')) return;
    setCleaning(true);
    setCleanResult(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/payments/cleanup-pending`, {}, { withCredentials: true });
      setCleanResult(r.data);
      await fetchSummary();
    } catch (e) {
      alert('Cleanup failed: ' + (e?.response?.data?.detail || 'unknown error'));
    }
    setCleaning(false);
  };

  const sendEmail = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/payments/reconciliation-email`, {}, { withCredentials: true });
      setSendResult(r.data);
    } catch (e) {
      alert('Send failed: ' + (e?.response?.data?.detail || 'unknown error'));
    }
    setSending(false);
  };

  const downloadCsv = () => {
    window.location.href = `${BACKEND_URL}/api/admin/payments/export.csv`;
  };

  return (
    <div className="space-y-4" data-testid="payment-reconciliation-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-bold text-[#0A0A0A] flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#BA1826]" /> Payment Reconciliation
          </h2>
          <p className="text-xs text-[#4B5563] mt-0.5">
            Live status across donations + shop orders. Only paid / refunded amounts count toward net confirmed revenue.
          </p>
        </div>
        <button onClick={fetchSummary} disabled={loading}
          className="text-xs px-2.5 py-1.5 rounded border border-[#E5E7EB] bg-white text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1 disabled:opacity-50"
          data-testid="reconciliation-refresh-btn">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading && !summary ? (
        <div className="flex items-center justify-center py-8 text-[#4B5563]"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : !summary ? (
        <p className="text-xs text-[#9CA3AF] bg-white border border-[#E5E7EB] rounded p-4 text-center">Could not load summary.</p>
      ) : (
        <>
          <div className="bg-[#0A0A0A] text-white rounded-lg p-5" data-testid="net-confirmed-revenue">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#FECACA] mb-1">Net Confirmed Revenue</p>
            <p className="font-heading text-3xl sm:text-4xl font-bold font-mono">{fmt(summary.net_confirmed_revenue)}</p>
            <p className="text-[10px] text-white/60 mt-2">= Total Paid − Total Refunded · As of {summary.period_label}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            <Stat icon={CheckCircle2} label="Paid" count={summary.paid.count} amount={summary.paid.amount} tone="green" testId="recon-paid" />
            <Stat icon={Clock} label="Pending" count={summary.pending.count} amount={summary.pending.amount} tone="amber" testId="recon-pending" />
            <Stat icon={XCircle} label="Failed" count={summary.failed.count} amount={summary.failed.amount} tone="red" testId="recon-failed" />
            <Stat icon={RefreshCw} label="Refunded" count={summary.refunded.count} amount={summary.refunded.amount} tone="gray" testId="recon-refunded" />
            <Stat icon={Clock} label="Expired" count={summary.expired.count} amount={summary.expired.amount} tone="gray" testId="recon-expired" />
            <Stat icon={Copy} label="Duplicates" count={summary.duplicate_pending.count} amount={summary.duplicate_pending.amount} tone="gray" testId="recon-duplicates" />
          </div>

          {summary.suspicious_activity && summary.suspicious_activity.length > 0 && (
            <div className="bg-red-50 border-l-4 border-red-600 rounded p-4" data-testid="suspicious-activity-alert">
              <h4 className="font-heading text-sm font-bold text-red-900 flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" /> Suspicious payment activity (last 7 days)
              </h4>
              <ul className="space-y-1 text-xs text-red-800">
                {summary.suspicious_activity.map((a, i) => (
                  <li key={i} className="font-mono">
                    <strong>{a.email}</strong> — {a.events} failed/refunded events
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
        <h3 className="font-heading text-sm font-bold text-[#0A0A0A] mb-3">Tools</h3>
        <div className="grid sm:grid-cols-3 gap-2">
          <button onClick={runCleanup} disabled={cleaning}
            className="text-xs font-bold px-3 py-2.5 rounded bg-[#BA1826] text-white hover:bg-[#9A1320] flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="cleanup-pending-btn">
            {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {cleaning ? 'Reconciling…' : 'Clean Up Pending Transactions'}
          </button>
          <button onClick={sendEmail} disabled={sending}
            className="text-xs font-semibold px-3 py-2.5 rounded border border-[#E5E7EB] bg-white text-[#0A0A0A] hover:bg-[#F8F9FA] flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="send-reconciliation-email-btn">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Send Reconciliation Email
          </button>
          <button onClick={downloadCsv}
            className="text-xs font-semibold px-3 py-2.5 rounded border border-[#E5E7EB] bg-white text-[#0A0A0A] hover:bg-[#F8F9FA] flex items-center justify-center gap-2"
            data-testid="export-payments-csv-btn">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>

        {cleanResult && (
          <div className="mt-3 bg-[#F8F9FA] border border-[#E5E7EB] rounded p-3 text-xs text-[#4B5563]" data-testid="cleanup-result">
            <p className="font-bold text-[#0A0A0A] mb-1">Cleanup complete</p>
            Checked: <strong>{cleanResult.checked}</strong> · Marked Paid: <strong className="text-green-700">{cleanResult.marked_paid}</strong> ·
            Expired: <strong className="text-gray-700">{cleanResult.marked_expired}</strong> ·
            Still Pending: <strong className="text-amber-700">{cleanResult.still_pending}</strong> ·
            Errors: <strong className="text-red-700">{cleanResult.errors}</strong>
          </div>
        )}
        {sendResult && (
          <div className={`mt-3 border rounded p-3 text-xs ${sendResult.sent ? 'bg-green-50 border-green-200 text-green-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`} data-testid="email-send-result">
            {sendResult.sent
              ? <>✓ Email sent to <strong>{sendResult.recipient}</strong>. Net confirmed revenue in report: <strong>{fmt(sendResult.summary.net_confirmed_revenue)}</strong></>
              : <>Send failed: {sendResult.reason}</>}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentReconciliationPanel;
